<script setup lang="ts">
import { ref } from 'vue'
import { usePalettePreviewStore, type PreviewPalette } from '~/stores/palettePreview'

const { t } = useI18n()
const previewEnabled = useState<boolean>('palette-preview-enabled', () => false)
const previewStore = usePalettePreviewStore()
const open = ref(true)

onMounted(() => {
    void previewStore.initialize()
})

const disable = async () => {
    if (await previewStore.disable()) previewEnabled.value = false
}

const swatches = (palette: PreviewPalette) => {
    const colors = palette.colors
    if (!colors) return []
    return [colors.body?.bg, colors.body?.color, colors.primary, colors.link, colors.menu?.bg, colors.menu?.text, colors.footer?.bg]
}
</script>

<template>
    <div class="palette-preview">
        <section v-if="open" class="palette-preview__panel" :aria-label="t('palette_preview_title')">
            <header class="palette-preview__header">
                <strong>{{ t('palette_preview_title') }}</strong>
                <button type="button" class="palette-preview__close" :aria-label="t('palette_preview_close')" @click="open = false">×</button>
            </header>
            <p class="palette-preview__hint">{{ t('palette_preview_hint') }}</p>
            <p v-if="previewStore.error" role="alert" class="palette-preview__error">{{ t('palette_preview_error') }}</p>
            <button v-if="previewStore.error" type="button" class="palette-preview__retry" :disabled="previewStore.loading" @click="previewStore.initialize()">
                {{ t('palette_preview_retry') }}
            </button>
            <div class="palette-preview__options" :aria-busy="previewStore.loading">
                <button
                    type="button"
                    class="palette-preview__option"
                    :class="{ 'is-active': previewStore.selectedId === null }"
                    :aria-pressed="previewStore.selectedId === null"
                    :disabled="previewStore.loading"
                    @click="previewStore.select(null)"
                >
                    {{ t('palette_preview_original') }}
                </button>
                <button
                    v-for="palette in previewStore.list || []"
                    :key="palette.id"
                    type="button"
                    class="palette-preview__option"
                    :class="{ 'is-active': previewStore.selectedId === palette.id }"
                    :aria-pressed="previewStore.selectedId === palette.id"
                    :disabled="previewStore.loading"
                    @click="previewStore.select(palette.id)"
                >
                    <span>{{ palette.name }}</span>
                    <span class="palette-preview__swatches" aria-hidden="true">
                        <span v-for="(color, index) in swatches(palette)" :key="index" :style="{ backgroundColor: color }" />
                    </span>
                </button>
                <p v-if="previewStore.list?.length === 0">{{ t('palette_preview_empty') }}</p>
                <p v-if="previewStore.loading">{{ t('palette_preview_loading') }}</p>
            </div>
            <button type="button" class="palette-preview__exit" :disabled="previewStore.loading" @click="disable">
                {{ t('palette_preview_exit') }}
            </button>
        </section>
        <button
            type="button"
            class="palette-preview__trigger"
            :aria-expanded="open"
            @click="open = !open"
        >
            <Icon name="lucide:palette" aria-hidden="true" />
            {{ t('palette_preview_title') }}
        </button>
    </div>
</template>

<style scoped>
.palette-preview {
    position: fixed;
    right: 20px;
    bottom: 20px;
    z-index: 10000;
    color: #17212f;
    font: 14px/1.4 system-ui, sans-serif;
}
.palette-preview__trigger {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-left: auto;
    padding: 11px 16px;
    border: 0;
    border-radius: 999px;
    background: #17212f;
    color: #fff;
    box-shadow: 0 8px 24px #0003;
    cursor: pointer;
}
.palette-preview__panel {
    width: min(360px, calc(100vw - 32px));
    max-height: calc(100dvh - 105px);
    overflow-y: auto;
    margin-bottom: 12px;
    padding: 18px;
    border-radius: 16px;
    background: #fff;
    box-shadow: 0 10px 36px #0004;
}
.palette-preview__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
}
.palette-preview__close {
    border: 0;
    background: transparent;
    color: #17212f;
    font-size: 24px;
    cursor: pointer;
}
.palette-preview__hint { color: #526071; }
.palette-preview__error { color: #b42318; }
.palette-preview__retry { border: 0; background: transparent; color: #234f9c; text-decoration: underline; cursor: pointer; }
.palette-preview__options { display: grid; gap: 8px; margin: 14px 0; }
.palette-preview__option {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    width: 100%;
    padding: 10px 12px;
    border: 1px solid #d2d8e0;
    border-radius: 10px;
    background: #fff;
    color: #17212f;
    text-align: left;
    cursor: pointer;
}
.palette-preview__option.is-active { border-color: #17212f; box-shadow: 0 0 0 1px #17212f; }
.palette-preview__option:disabled, .palette-preview__exit:disabled { opacity: .6; cursor: wait; }
.palette-preview__swatches { display: flex; flex: none; overflow: hidden; border-radius: 5px; }
.palette-preview__swatches span { width: 14px; height: 20px; }
.palette-preview__exit {
    width: 100%;
    padding: 10px;
    border: 1px solid #d2d8e0;
    border-radius: 10px;
    background: #f5f6f8;
    color: #17212f;
    cursor: pointer;
}
@media (max-width: 600px) {
    .palette-preview { right: 16px; bottom: 16px; }
}
</style>
